import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Check,
  AlertCircle,
  Loader2,
  Building,
  ChevronDown,
  ChevronUp,
  Archive,
  RotateCcw,
  AlertTriangle,
  Upload,
  Image,
  BookOpen,
  Wallet,
  Clock,
  XCircle,
  Palette,
  Pipette,
  Info,
} from 'lucide-react';
import { API_BASE_URL } from '@/utils/api';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import {
  fetchEtablissementSettings,
  updateEtablissementSettings,
  createCampus,
  updateCampus,
  deleteCampus,
  toggleCampusStatus,
  uploadLogo,
  deleteLogo,
  updateBrandColors,
  SigleType,
  type EtablissementSettings,
  type Campus,
  type CreateCampusDto,
  type UpdateCampusDto,
} from '@/services/etablissementService';
import {
  type SolutionType,
  type SolutionChangeRequest,
  getMyRequests,
  createSolutionRequest,
  cancelRequest,
} from '@/services/solutionRequestsService';

interface CampusFormData {
  name: string;
  sigle: string;
  address: string;
  city: string;
  postalCode: string;
  email: string;
  receptionPhoneCountry: string;
  receptionPhoneNumber: string;
  isMainCampus: boolean;
}

const emptyCampusForm: CampusFormData = {
  name: '',
  sigle: '',
  address: '',
  city: '',
  postalCode: '',
  email: '',
  receptionPhoneCountry: '+212',
  receptionPhoneNumber: '',
  isMainCampus: false,
};

// Validation helpers
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidPhone = (phone: string): boolean => {
  // Basic phone validation: 9-15 digits, optionally starting with +
  const phoneRegex = /^\+?[0-9]{9,15}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

// Form validation function
const isCampusFormValid = (form: CampusFormData): boolean => {
  // Required fields must not be empty
  const hasRequiredFields = Boolean(
    form.name?.trim() &&
    form.address?.trim() &&
    form.city?.trim() &&
    form.postalCode?.trim() &&
    form.email?.trim() &&
    form.receptionPhoneNumber?.trim()
  );

  if (!hasRequiredFields) return false;

  // Email must be valid
  if (!isValidEmail(form.email)) return false;

  // Phone must be valid
  if (!isValidPhone(form.receptionPhoneNumber)) return false;

  return true;
};

// Validate form and return errors
const validateCampusForm = (form: CampusFormData): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!form.name?.trim()) errors.name = 'Le nom est requis';
  if (!form.address?.trim()) errors.address = 'L\'adresse est requise';
  if (!form.city?.trim()) errors.city = 'La ville est requise';
  if (!form.postalCode?.trim()) errors.postalCode = 'Le code postal est requis';
  if (!form.email?.trim()) errors.email = 'L\'email est requis';
  else if (!isValidEmail(form.email)) errors.email = 'Email invalide';
  if (!form.receptionPhoneNumber?.trim()) errors.receptionPhoneNumber = 'Le telephone est requis';
  else if (!isValidPhone(form.receptionPhoneNumber)) errors.receptionPhoneNumber = 'Numero de telephone invalide';

  return errors;
};

// Liste des indicatifs téléphoniques les plus courants
const COUNTRY_CODES = [
  { code: '+212', country: 'Maroc', flag: '🇲🇦' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+34', country: 'Espagne', flag: '🇪🇸' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'Royaume-Uni', flag: '🇬🇧' },
  { code: '+49', country: 'Allemagne', flag: '🇩🇪' },
  { code: '+39', country: 'Italie', flag: '🇮🇹' },
  { code: '+32', country: 'Belgique', flag: '🇧🇪' },
  { code: '+41', country: 'Suisse', flag: '🇨🇭' },
  { code: '+31', country: 'Pays-Bas', flag: '🇳🇱' },
  { code: '+216', country: 'Tunisie', flag: '🇹🇳' },
  { code: '+213', country: 'Algérie', flag: '🇩🇿' },
  { code: '+966', country: 'Arabie Saoudite', flag: '🇸🇦' },
  { code: '+971', country: 'Émirats Arabes Unis', flag: '🇦🇪' },
];

// Modal de confirmation générique
interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning' | 'info';
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  variant = 'warning',
}) => {
  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      icon: <AlertTriangle className="w-6 h-6 text-red-600" />,
      iconBg: 'bg-red-100',
      confirmBtn: 'bg-red-600 hover:bg-red-700',
    },
    warning: {
      icon: <AlertCircle className="w-6 h-6 text-amber-600" />,
      iconBg: 'bg-amber-100',
      confirmBtn: 'bg-amber-600 hover:bg-amber-700',
    },
    info: {
      icon: <AlertCircle className="w-6 h-6 text-blue-600" />,
      iconBg: 'bg-blue-100',
      confirmBtn: 'bg-blue-600 hover:bg-blue-700',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onCancel} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-full ${styles.iconBg}`}>
              {styles.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
              <div className="text-sm text-gray-600">{message}</div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 text-white rounded-lg transition-colors ${styles.confirmBtn}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Solution configuration for display
// Note: Marketing solution is not yet developed, will be added later
const solutionConfig = {
  academic: {
    label: 'Gestion Academique',
    description: 'Filieres, cours, notes, emplois du temps',
    icon: BookOpen,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    iconColor: 'text-blue-600',
  },
  finance: {
    label: 'Gestion Financiere',
    description: 'Paiements, factures, bourses, paie',
    icon: Wallet,
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    iconColor: 'text-green-600',
  },
};

export const GestionEtablissementPage: React.FC = () => {
  const { tokens } = useAuth();
  const { updateTenantLogo, user } = useAuthStore();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.INSTITUTION_PROFILE_MANAGE);
  const token = tokens?.accessToken;

  // Get enabled solutions from user
  const enabledSolutions = user?.enabledSolutions || [];
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Establishment data
  const [settings, setSettings] = useState<EtablissementSettings | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameForm, setNameForm] = useState({ name: '', sigleType: SigleType.COMMON, commonSigle: '', isUniversity: false });

  // Campus management
  const [showAddCampus, setShowAddCampus] = useState(false);
  const [editingCampusId, setEditingCampusId] = useState<string | null>(null);
  const [campusForm, setCampusForm] = useState<CampusFormData>(emptyCampusForm);
  const [expandedCampus, setExpandedCampus] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Modal states
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; campus: Campus | null }>({ isOpen: false, campus: null });
  const [archiveModal, setArchiveModal] = useState<{ isOpen: boolean; campus: Campus | null }>({ isOpen: false, campus: null });
  const [mainCampusModal, setMainCampusModal] = useState<{ isOpen: boolean; currentMain: Campus | null }>({ isOpen: false, currentMain: null });
  const [showArchivedCampuses, setShowArchivedCampuses] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deleteLogoModal, setDeleteLogoModal] = useState(false);

  // Brand colors state (charte graphique)
  const [editingBrandColors, setEditingBrandColors] = useState(false);
  const [brandColorsForm, setBrandColorsForm] = useState({ primaryColor: '#1E40AF', secondaryColor: '#6366F1' });
  const [savingBrandColors, setSavingBrandColors] = useState(false);

  // Solution request states
  const [solutionRequests, setSolutionRequests] = useState<SolutionChangeRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [solutionModal, setSolutionModal] = useState<{
    isOpen: boolean;
    solution: SolutionType | null;
    action: 'ACTIVATE' | 'DEACTIVATE' | null;
  }>({ isOpen: false, solution: null, action: null });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [cancellingRequest, setCancellingRequest] = useState<string | null>(null);

  // Load settings on mount
  const loadSettings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEtablissementSettings(token);
      setSettings(data);
      setNameForm({
        name: data.name,
        sigleType: data.sigleType,
        commonSigle: data.commonSigle || '',
        isUniversity: data.isUniversity || false,
      });
      setBrandColorsForm({
        primaryColor: data.primaryColor || '#1E40AF',
        secondaryColor: data.secondaryColor || '#6366F1',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Load solution requests
  const loadSolutionRequests = useCallback(async () => {
    if (!token) return;
    setLoadingRequests(true);
    try {
      const requests = await getMyRequests();
      setSolutionRequests(requests);
    } catch (err) {
      console.error('Failed to load solution requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
    loadSolutionRequests();
  }, [loadSettings, loadSolutionRequests]);

  const handleSaveSettings = async () => {
    if (!token) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await updateEtablissementSettings(token, nameForm);
      setSuccess(result.message);
      setEditingName(false);
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !token) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setError('Format non supporté. Utilisez PNG, JPG, WebP ou SVG.');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setError('Le fichier est trop volumineux. Taille maximum: 2MB.');
      return;
    }

    setUploadingLogo(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await uploadLogo(token, file);
      setSuccess(result.message);
      // Update the logo in auth store so Sidebar updates immediately
      updateTenantLogo(result.data.logoUrl);
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du téléchargement du logo');
    } finally {
      setUploadingLogo(false);
      // Reset input
      event.target.value = '';
    }
  };

  // Handle logo deletion
  const handleDeleteLogo = async () => {
    if (!token) return;
    setUploadingLogo(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await deleteLogo(token);
      setSuccess(result.message);
      setDeleteLogoModal(false);
      // Update the logo in auth store so Sidebar updates immediately
      updateTenantLogo(undefined);
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handle brand colors update (charte graphique)
  const handleSaveBrandColors = async () => {
    if (!token) return;
    setSavingBrandColors(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await updateBrandColors(token, brandColorsForm);
      setSuccess(result.message);
      setEditingBrandColors(false);
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise a jour des couleurs');
    } finally {
      setSavingBrandColors(false);
    }
  };

  // Vérifier si c'est le premier campus ou s'il n'y a pas de campus principal
  const shouldPreCheckMainCampus = (): boolean => {
    if (!settings) return true;
    const activeCampuses = settings.campuses.filter(c => c.isActive);
    if (activeCampuses.length === 0) return true;
    const hasMainCampus = activeCampuses.some(c => c.isMainCampus);
    return !hasMainCampus;
  };

  // Trouver le campus principal actuel
  const getCurrentMainCampus = (): Campus | null => {
    if (!settings) return null;
    return settings.campuses.find(c => c.isMainCampus && c.isActive) || null;
  };

  const handleAddCampus = async () => {
    if (!token) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const dto: CreateCampusDto = {
        name: campusForm.name,
        address: campusForm.address,
        city: campusForm.city,
        postalCode: campusForm.postalCode,
        email: campusForm.email,
        receptionPhoneCountry: campusForm.receptionPhoneCountry,
        receptionPhoneNumber: campusForm.receptionPhoneNumber,
        isMainCampus: campusForm.isMainCampus,
      };
      if (nameForm.sigleType === SigleType.PER_CAMPUS && campusForm.sigle) {
        dto.sigle = campusForm.sigle;
      }
      const result = await createCampus(token, dto);
      setSuccess(result.message);
      setShowAddCampus(false);
      setCampusForm(emptyCampusForm);
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création du campus');
    } finally {
      setSaving(false);
    }
  };

  // Gérer le changement de campus principal avec confirmation
  const handleMainCampusChange = (checked: boolean) => {
    if (checked) {
      const currentMain = getCurrentMainCampus();
      if (currentMain && !editingCampusId) {
        // Si on crée un nouveau campus et qu'il y a déjà un principal
        setMainCampusModal({ isOpen: true, currentMain });
      } else if (currentMain && editingCampusId && currentMain.id !== editingCampusId) {
        // Si on édite un campus différent du principal actuel
        setMainCampusModal({ isOpen: true, currentMain });
      } else {
        setCampusForm({ ...campusForm, isMainCampus: true });
      }
    } else {
      setCampusForm({ ...campusForm, isMainCampus: false });
    }
  };

  const confirmMainCampusChange = () => {
    setCampusForm({ ...campusForm, isMainCampus: true });
    setMainCampusModal({ isOpen: false, currentMain: null });
  };

  const handleUpdateCampus = async (campusId: string) => {
    if (!token) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const dto: UpdateCampusDto = {
        name: campusForm.name,
        address: campusForm.address,
        city: campusForm.city,
        postalCode: campusForm.postalCode,
        email: campusForm.email,
        receptionPhoneCountry: campusForm.receptionPhoneCountry,
        receptionPhoneNumber: campusForm.receptionPhoneNumber,
        isMainCampus: campusForm.isMainCampus,
      };
      if (nameForm.sigleType === SigleType.PER_CAMPUS) {
        dto.sigle = campusForm.sigle;
      }
      const result = await updateCampus(token, campusId, dto);
      setSuccess(result.message);
      setEditingCampusId(null);
      setCampusForm(emptyCampusForm);
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Archiver un campus (désactiver)
  const handleArchiveCampus = async () => {
    if (!token || !archiveModal.campus) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await toggleCampusStatus(token, archiveModal.campus.id);
      setSuccess(`Campus "${archiveModal.campus.name}" archivé avec succès. Vous pouvez le retrouver dans la section "Campus archivés".`);
      setArchiveModal({ isOpen: false, campus: null });
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'archivage');
    } finally {
      setSaving(false);
    }
  };

  // Restaurer un campus archivé
  const handleRestoreCampus = async (campusId: string) => {
    if (!token) return;
    try {
      const result = await toggleCampusStatus(token, campusId);
      setSuccess(result.message);
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la restauration');
    }
  };

  // Supprimer définitivement un campus
  const handleDeleteCampus = async () => {
    if (!token || !deleteModal.campus) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await deleteCampus(token, deleteModal.campus.id);
      setSuccess(result.message);
      setDeleteModal({ isOpen: false, campus: null });
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  // Get pending request for a specific solution
  const getPendingRequestForSolution = (solution: SolutionType): SolutionChangeRequest | undefined => {
    return solutionRequests.find(
      (r) => r.solution === solution && r.status === 'SOLUTION_PENDING'
    );
  };

  // Handle clicking on a solution card
  const handleSolutionClick = (solution: SolutionType) => {
    if (!canManage) return;

    const isActive = enabledSolutions.includes(solution);
    const hasPendingRequest = getPendingRequestForSolution(solution);

    // If there's a pending request, don't allow new action
    if (hasPendingRequest) return;

    // Check if trying to deactivate the last solution
    if (isActive && enabledSolutions.length === 1) {
      setError('Vous ne pouvez pas desactiver la derniere solution active. Au moins une solution doit rester active.');
      return;
    }

    setSolutionModal({
      isOpen: true,
      solution,
      action: isActive ? 'DEACTIVATE' : 'ACTIVATE',
    });
  };

  // Submit solution request
  const handleSubmitSolutionRequest = async () => {
    if (!solutionModal.solution || !solutionModal.action) return;

    setSubmittingRequest(true);
    setError(null);
    setSuccess(null);

    try {
      await createSolutionRequest({
        solution: solutionModal.solution,
        requestType: solutionModal.action,
      });

      setSuccess(
        solutionModal.action === 'ACTIVATE'
          ? `Demande d'activation de "${solutionConfig[solutionModal.solution].label}" envoyee. Vous serez notifie de la decision du Super Admin.`
          : `Demande de desactivation de "${solutionConfig[solutionModal.solution].label}" envoyee. Vous serez notifie de la decision du Super Admin.`
      );
      setSolutionModal({ isOpen: false, solution: null, action: null });
      await loadSolutionRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la soumission de la demande');
    } finally {
      setSubmittingRequest(false);
    }
  };

  // Cancel a pending request
  const handleCancelRequest = async (requestId: string) => {
    setCancellingRequest(requestId);
    setError(null);
    setSuccess(null);

    try {
      await cancelRequest(requestId);
      setSuccess('Demande annulee avec succes.');
      await loadSolutionRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'annulation de la demande');
    } finally {
      setCancellingRequest(null);
    }
  };

  const startEditCampus = (campus: Campus) => {
    setEditingCampusId(campus.id);
    setCampusForm({
      name: campus.name,
      sigle: campus.sigle || '',
      address: campus.address,
      city: campus.city,
      postalCode: campus.postalCode,
      email: campus.email,
      receptionPhoneCountry: campus.receptionPhoneCountry || '+212',
      receptionPhoneNumber: campus.receptionPhoneNumber,
      isMainCampus: campus.isMainCampus,
    });
    setExpandedCampus(campus.id);
    setFormErrors({});
  };

  const cancelEdit = () => {
    setEditingCampusId(null);
    setShowAddCampus(false);
    setCampusForm(emptyCampusForm);
    setFormErrors({});
  };

  // Ouvrir le formulaire d'ajout avec pré-cochage si nécessaire
  const openAddCampusForm = () => {
    const shouldPreCheck = shouldPreCheckMainCampus();
    setShowAddCampus(true);
    setCampusForm({
      ...emptyCampusForm,
      isMainCampus: shouldPreCheck,
    });
    setEditingCampusId(null);
    setFormErrors({});
  };

  // Séparer les campus actifs et archivés
  const activeCampuses = settings?.campuses.filter(c => c.isActive) || [];
  const archivedCampuses = settings?.campuses.filter(c => !c.isActive) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profil Etablissement</h1>
          <p className="text-gray-500 mt-1">
            Gerez les informations de votre etablissement et ses campus
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Erreur</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-5 h-5 text-red-600" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-800">Succès</p>
            <p className="text-sm text-green-700">{success}</p>
          </div>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="w-5 h-5 text-green-600" />
          </button>
        </div>
      )}

      {/* Logo Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Image className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Logo de l'Établissement</h2>
              <p className="text-sm text-gray-500">Ce logo remplacera le logo NEXEDU pour tous les utilisateurs de votre établissement</p>
            </div>
          </div>
          {settings?.logoUrl && canManage && (
            <button
              onClick={() => setDeleteLogoModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              disabled={uploadingLogo}
            >
              <Trash2 className="w-4 h-4" />
              Supprimer le logo
            </button>
          )}
        </div>

        <div className="p-6">
          <div className="flex items-start gap-6">
            {/* Logo Preview */}
            <div className="flex-shrink-0">
              {settings?.logoUrl ? (
                canManage ? (
                  <label className="relative group cursor-pointer block">
                    <img
                      src={`${API_BASE_URL}${settings.logoUrl}`}
                      alt="Logo de l'établissement"
                      className="w-32 h-32 object-contain border border-gray-200 rounded-lg bg-gray-50"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      {uploadingLogo ? (
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      ) : (
                        <Pencil className="w-6 h-6 text-white" />
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploadingLogo}
                    />
                  </label>
                ) : (
                  <img
                    src={`${API_BASE_URL}${settings.logoUrl}`}
                    alt="Logo de l'établissement"
                    className="w-32 h-32 object-contain border border-gray-200 rounded-lg bg-gray-50"
                  />
                )
              ) : canManage ? (
                <label className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
                  {uploadingLogo ? (
                    <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-xs text-gray-500 text-center px-2">Cliquez pour ajouter</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={uploadingLogo}
                  />
                </label>
              ) : (
                <div className="w-32 h-32 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center bg-gray-50">
                  <Image className="w-8 h-8 text-gray-300 mb-2" />
                  <span className="text-xs text-gray-400 text-center px-2">Pas de logo</span>
                </div>
              )}
            </div>

            {/* Upload Instructions */}
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Recommandations</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                  <strong>Résolution optimale:</strong> 200x200px (carré) ou 400x100px (rectangulaire)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                  <strong>Formats acceptés:</strong> PNG, JPG, WebP, SVG
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                  <strong>Taille maximale:</strong> 2 MB
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                  Utilisez un fond transparent (PNG/SVG) pour un meilleur rendu
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Charte Graphique Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Palette className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Charte Graphique</h2>
              <p className="text-sm text-gray-500">Personnalisez les couleurs de votre etablissement (page de connexion)</p>
            </div>
          </div>
          {!editingBrandColors && canManage && (
            <button
              onClick={() => setEditingBrandColors(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Pencil className="w-4 h-4" />
              Modifier
            </button>
          )}
        </div>

        <div className="p-6">
          {editingBrandColors ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Primary Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Couleur Primaire
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={brandColorsForm.primaryColor}
                      onChange={(e) => setBrandColorsForm({ ...brandColorsForm, primaryColor: e.target.value })}
                      className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if ('EyeDropper' in window) {
                          try {
                            // @ts-expect-error EyeDropper API not in TypeScript types yet
                            const eyeDropper = new window.EyeDropper();
                            const result = await eyeDropper.open();
                            setBrandColorsForm({ ...brandColorsForm, primaryColor: result.sRGBHex });
                          } catch {
                            // User cancelled or error
                          }
                        } else {
                          alert('Votre navigateur ne supporte pas la pipette. Utilisez Chrome ou Edge.');
                        }
                      }}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Pipette - Cliquer pour selectionner une couleur"
                    >
                      <Pipette className="w-5 h-5 text-gray-600" />
                    </button>
                    <input
                      type="text"
                      value={brandColorsForm.primaryColor}
                      onChange={(e) => setBrandColorsForm({ ...brandColorsForm, primaryColor: e.target.value })}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
                      placeholder="#1E40AF"
                      maxLength={7}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Couleur de fond de la page de connexion</p>
                </div>

                {/* Secondary Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Couleur Secondaire
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={brandColorsForm.secondaryColor}
                      onChange={(e) => setBrandColorsForm({ ...brandColorsForm, secondaryColor: e.target.value })}
                      className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if ('EyeDropper' in window) {
                          try {
                            // @ts-expect-error EyeDropper API not in TypeScript types yet
                            const eyeDropper = new window.EyeDropper();
                            const result = await eyeDropper.open();
                            setBrandColorsForm({ ...brandColorsForm, secondaryColor: result.sRGBHex });
                          } catch {
                            // User cancelled or error
                          }
                        } else {
                          alert('Votre navigateur ne supporte pas la pipette. Utilisez Chrome ou Edge.');
                        }
                      }}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Pipette - Cliquer pour selectionner une couleur"
                    >
                      <Pipette className="w-5 h-5 text-gray-600" />
                    </button>
                    <input
                      type="text"
                      value={brandColorsForm.secondaryColor}
                      onChange={(e) => setBrandColorsForm({ ...brandColorsForm, secondaryColor: e.target.value })}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
                      placeholder="#6366F1"
                      maxLength={7}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Couleur du bouton et des liens</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSaveBrandColors}
                  disabled={savingBrandColors}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {savingBrandColors ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Enregistrer
                </button>
                <button
                  onClick={() => {
                    setEditingBrandColors(false);
                    setBrandColorsForm({
                      primaryColor: settings?.primaryColor || '#1E40AF',
                      secondaryColor: settings?.secondaryColor || '#6366F1',
                    });
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              {/* Color Swatches */}
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <div
                    className="w-32 h-20 rounded-xl shadow-md border border-gray-200"
                    style={{ backgroundColor: settings?.primaryColor || '#1E40AF' }}
                  />
                  <p className="mt-2 text-sm font-medium text-gray-600">Fond</p>
                  <p className="text-xs text-gray-400 uppercase">{settings?.primaryColor || '#1E40AF'}</p>
                </div>
                <div className="text-center">
                  <div
                    className="w-32 h-20 rounded-xl shadow-md border border-gray-200"
                    style={{ backgroundColor: settings?.secondaryColor || '#6366F1' }}
                  />
                  <p className="mt-2 text-sm font-medium text-gray-600">Boutons</p>
                  <p className="text-xs text-gray-400 uppercase">{settings?.secondaryColor || '#6366F1'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Solutions Actives Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <BookOpen className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Solutions Actives</h2>
              <p className="text-sm text-gray-500">Modules actifs pour votre etablissement</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {loadingRequests ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {Object.entries(solutionConfig).map(([key, config]) => {
                  const solutionKey = key as SolutionType;
                  const isActive = enabledSolutions.includes(key);
                  const pendingRequest = getPendingRequestForSolution(solutionKey);
                  const Icon = config.icon;
                  const isClickable = canManage && !pendingRequest;
                  const isLastActive = isActive && enabledSolutions.length === 1;

                  return (
                    <div
                      key={key}
                      onClick={() => isClickable && !isLastActive && handleSolutionClick(solutionKey)}
                      className={`relative rounded-xl border-2 p-4 transition-all ${
                        isActive
                          ? `${config.bgColor} border-${key === 'academic' ? 'blue' : key === 'finance' ? 'green' : 'purple'}-300`
                          : 'bg-gray-50 border-gray-200 opacity-60'
                      } ${isClickable && !isLastActive ? 'cursor-pointer hover:shadow-md' : ''} ${
                        pendingRequest ? 'ring-2 ring-amber-400' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${isActive ? 'bg-white' : 'bg-gray-200'}`}>
                          <Icon className={`w-5 h-5 ${isActive ? config.iconColor : 'text-gray-400'}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className={`font-semibold ${isActive ? config.textColor : 'text-gray-500'}`}>
                            {config.label}
                          </h3>
                          <p className={`text-sm mt-0.5 ${isActive ? 'text-gray-600' : 'text-gray-400'}`}>
                            {config.description}
                          </p>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="absolute top-3 right-3">
                        {pendingRequest ? (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 rounded-full text-xs font-medium text-amber-700">
                            <Clock className="w-3 h-3" />
                            {pendingRequest.requestType === 'ACTIVATE' ? 'Activation' : 'Desactivation'} en attente
                          </span>
                        ) : isActive ? (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-white rounded-full text-xs font-medium text-green-700">
                            <Check className="w-3 h-3" />
                            Actif
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-gray-200 rounded-full text-xs font-medium text-gray-500">
                            Non actif
                          </span>
                        )}
                      </div>

                      {/* Cancel pending request button */}
                      {pendingRequest && canManage && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelRequest(pendingRequest.id);
                            }}
                            disabled={cancellingRequest === pendingRequest.id}
                            className="flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
                          >
                            {cancellingRequest === pendingRequest.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <XCircle className="w-3 h-3" />
                            )}
                            Annuler la demande
                          </button>
                        </div>
                      )}

                      {/* Hint for clickable cards */}
                      {isClickable && !pendingRequest && (
                        <p className="mt-3 text-xs text-gray-400">
                          {isActive
                            ? isLastActive
                              ? 'Derniere solution active'
                              : 'Cliquez pour demander la desactivation'
                            : 'Cliquez pour demander l\'activation'}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Info text */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  {canManage
                    ? 'Cliquez sur une solution pour demander son activation ou desactivation.'
                    : 'Contactez votre administrateur pour modifier les solutions.'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Establishment Settings Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Building2 className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="font-semibold text-gray-900">Informations de l'Établissement</h2>
          </div>
          {!editingName && canManage && (
            <button
              onClick={() => setEditingName(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Pencil className="w-4 h-4" />
              Modifier
            </button>
          )}
        </div>

        <div className="p-6">
          {editingName ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de l'établissement
                </label>
                <input
                  type="text"
                  value={nameForm.name}
                  onChange={(e) => setNameForm({ ...nameForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ex: HEMS Business School"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de sigle
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sigleType"
                      checked={nameForm.sigleType === SigleType.COMMON}
                      onChange={() => setNameForm({ ...nameForm, sigleType: SigleType.COMMON })}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Sigle commun pour tous les campus</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sigleType"
                      checked={nameForm.sigleType === SigleType.PER_CAMPUS}
                      onChange={() => setNameForm({ ...nameForm, sigleType: SigleType.PER_CAMPUS })}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Sigle différent par campus</span>
                  </label>
                </div>
              </div>

              {nameForm.sigleType === SigleType.COMMON && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sigle commun
                  </label>
                  <input
                    type="text"
                    value={nameForm.commonSigle}
                    onChange={(e) => setNameForm({ ...nameForm, commonSigle: e.target.value })}
                    className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Ex: HEMS"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type d'établissement
                </label>
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={nameForm.isUniversity}
                        onChange={(e) => setNameForm({ ...nameForm, isUniversity: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        Votre établissement est-il une université ?
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {nameForm.isUniversity
                          ? 'Oui, c\'est une université'
                          : 'Non, ce n\'est pas une université (école, institut, etc.)'}
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Enregistrer
                </button>
                <button
                  onClick={() => {
                    setEditingName(false);
                    if (settings) {
                      setNameForm({
                        name: settings.name,
                        sigleType: settings.sigleType,
                        commonSigle: settings.commonSigle || '',
                        isUniversity: settings.isUniversity || false,
                      });
                    }
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-500">Nom de l'établissement</span>
                <p className="font-medium text-gray-900">{settings?.name}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Type d'établissement</span>
                <p className="font-medium text-gray-900">
                  {settings?.isUniversity ? 'Université' : 'École / Institut'}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Type de sigle</span>
                <p className="font-medium text-gray-900">
                  {settings?.sigleType === SigleType.COMMON
                    ? `Commun: ${settings?.commonSigle || 'Non défini'}`
                    : 'Différent par campus'}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Nombre de campus actifs</span>
                <p className="font-medium text-gray-900">{activeCampuses.length}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Campuses Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Building className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="font-semibold text-gray-900">Campus</h2>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-full">
              {activeCampuses.length}
            </span>
          </div>
          {!showAddCampus && canManage && (
            <button
              onClick={openAddCampusForm}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter un campus
            </button>
          )}
        </div>

        {activeCampuses.some(c => c.wasAutoCreated) && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-900">
              Un campus a été créé automatiquement avec des informations par défaut. Cliquez dessus pour
              compléter son nom, son adresse, son téléphone et son email&nbsp;— ces données sont nécessaires
              pour l'édition de documents officiels.
            </p>
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {/* Add Campus Form */}
          {showAddCampus && (
            <div className="p-6 bg-blue-50">
              <h3 className="font-medium text-gray-900 mb-4">Nouveau Campus</h3>
              <CampusForm
                form={campusForm}
                setForm={setCampusForm}
                showSigle={nameForm.sigleType === SigleType.PER_CAMPUS}
                onSave={handleAddCampus}
                onCancel={cancelEdit}
                saving={saving}
                onMainCampusChange={handleMainCampusChange}
                isFirstCampus={shouldPreCheckMainCampus()}
                formErrors={formErrors}
                setFormErrors={setFormErrors}
              />
            </div>
          )}

          {/* Campus List */}
          {activeCampuses.map((campus) => (
            <div key={campus.id} className="border-b border-gray-100 last:border-b-0">
              {/* Campus Header */}
              <div
                className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedCampus(expandedCampus === campus.id ? null : campus.id)}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${campus.isMainCampus ? 'bg-amber-100' : 'bg-gray-100'}`}>
                    <MapPin className={`w-5 h-5 ${campus.isMainCampus ? 'text-amber-600' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{campus.name}</span>
                      {campus.isMainCampus && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                          Principal
                        </span>
                      )}
                      {campus.wasAutoCreated && (
                        <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-300 text-xs rounded-full">
                          À compléter
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{campus.city}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {expandedCampus === campus.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Campus Details (Expanded) */}
              {expandedCampus === campus.id && (
                <div className="px-6 pb-6 bg-gray-50">
                  {editingCampusId === campus.id ? (
                    <CampusForm
                      form={campusForm}
                      setForm={setCampusForm}
                      showSigle={nameForm.sigleType === SigleType.PER_CAMPUS}
                      onSave={() => handleUpdateCampus(campus.id)}
                      onCancel={cancelEdit}
                      saving={saving}
                      onMainCampusChange={handleMainCampusChange}
                      isFirstCampus={false}
                      formErrors={formErrors}
                      setFormErrors={setFormErrors}
                    />
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {nameForm.sigleType === SigleType.PER_CAMPUS && (
                          <div>
                            <span className="text-sm text-gray-500">Sigle</span>
                            <p className="font-medium text-gray-900">{campus.sigle || '-'}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-sm text-gray-500">Adresse</span>
                          <p className="font-medium text-gray-900">{campus.address}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Ville</span>
                          <p className="font-medium text-gray-900">{campus.city}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Code Postal</span>
                          <p className="font-medium text-gray-900">{campus.postalCode}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <div>
                            <span className="text-sm text-gray-500">Email</span>
                            <p className="font-medium text-gray-900">{campus.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <div>
                            <span className="text-sm text-gray-500">Numéro de réception</span>
                            <p className="font-medium text-gray-900">{campus.receptionPhoneCountry} {campus.receptionPhoneNumber}</p>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      {canManage && (
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => startEditCampus(campus)}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                            Modifier
                          </button>
                          <button
                            onClick={() => setArchiveModal({ isOpen: true, campus })}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          >
                            <Archive className="w-4 h-4" />
                            Archiver
                          </button>
                          <button
                            onClick={() => setDeleteModal({ isOpen: true, campus })}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Empty State */}
          {activeCampuses.length === 0 && !showAddCampus && (
            <div className="p-12 text-center">
              <Building className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900 mb-1">Aucun campus</h3>
              <p className="text-gray-500 mb-4">
                {canManage ? 'Commencez par ajouter votre premier campus' : 'Aucun campus configuré'}
              </p>
              {canManage && (
                <button
                  onClick={openAddCampusForm}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un campus
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Archived Campuses Section */}
      {archivedCampuses.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div
            className="px-6 py-4 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => setShowArchivedCampuses(!showArchivedCampuses)}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Archive className="w-5 h-5 text-gray-600" />
              </div>
              <h2 className="font-semibold text-gray-900">Campus archivés</h2>
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-full">
                {archivedCampuses.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {showArchivedCampuses ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {showArchivedCampuses && (
            <div className="divide-y divide-gray-100">
              <div className="px-6 py-3 bg-amber-50 border-b border-amber-100">
                <p className="text-sm text-amber-800">
                  Les campus archivés conservent toutes leurs données (staff, étudiants, parents, professeurs, etc.).
                  Vous pouvez les restaurer à tout moment.
                </p>
              </div>
              {archivedCampuses.map((campus) => (
                <div key={campus.id} className="px-6 py-4 flex items-center justify-between opacity-75">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <MapPin className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">{campus.name}</span>
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                          Archivé
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{campus.city}</p>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleRestoreCampus(campus.id)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Restaurer
                      </button>
                      <button
                        onClick={() => setDeleteModal({ isOpen: true, campus })}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Supprimer définitivement
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal: Changement de campus principal */}
      <ConfirmModal
        isOpen={mainCampusModal.isOpen}
        title="Changer le campus principal"
        message={
          <div>
            <p className="mb-2">
              Si vous sélectionnez ce campus comme principal, le campus{' '}
              <strong>"{mainCampusModal.currentMain?.name}"</strong> ne sera plus le campus principal.
            </p>
            <p>Voulez-vous continuer ?</p>
          </div>
        }
        confirmText="Oui, changer"
        cancelText="Annuler"
        onConfirm={confirmMainCampusChange}
        onCancel={() => setMainCampusModal({ isOpen: false, currentMain: null })}
        variant="warning"
      />

      {/* Modal: Archiver un campus */}
      <ConfirmModal
        isOpen={archiveModal.isOpen}
        title="Archiver le campus"
        message={
          <div>
            <p className="mb-2">
              Êtes-vous sûr de vouloir archiver le campus <strong>"{archiveModal.campus?.name}"</strong> ?
            </p>
            <p className="mb-2">
              Les données du campus (staff, étudiants, parents, professeurs, etc.) seront conservées.
            </p>
            <p className="text-amber-700 font-medium">
              Vous pourrez restaurer ce campus à tout moment depuis la section "Campus archivés".
            </p>
          </div>
        }
        confirmText="Archiver"
        cancelText="Annuler"
        onConfirm={handleArchiveCampus}
        onCancel={() => setArchiveModal({ isOpen: false, campus: null })}
        variant="warning"
      />

      {/* Modal: Supprimer un campus */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        title="Supprimer le campus"
        message={
          <div>
            <p className="mb-3 font-semibold text-red-700">
              Attention : Cette action est irréversible !
            </p>
            <p className="mb-2">
              Supprimer le campus <strong>"{deleteModal.campus?.name}"</strong> signifie que vous allez supprimer
              définitivement toutes les informations associées :
            </p>
            <ul className="list-disc list-inside mb-3 text-gray-700">
              <li>Staff administratif</li>
              <li>Étudiants</li>
              <li>Parents/Tuteurs</li>
              <li>Professeurs</li>
              <li>Cours et emplois du temps</li>
              <li>Toutes les autres données liées</li>
            </ul>
            <p className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
              <strong>Conseil :</strong> Si vous souhaitez conserver ces informations, utilisez plutôt
              l'option "Archiver" qui préserve toutes les données.
            </p>
            <p className="font-semibold">Voulez-vous vraiment supprimer ce campus ?</p>
          </div>
        }
        confirmText="Oui, supprimer définitivement"
        cancelText="Annuler"
        onConfirm={handleDeleteCampus}
        onCancel={() => setDeleteModal({ isOpen: false, campus: null })}
        variant="danger"
      />

      {/* Modal: Supprimer le logo */}
      <ConfirmModal
        isOpen={deleteLogoModal}
        title="Supprimer le logo"
        message={
          <div>
            <p className="mb-2">
              Êtes-vous sûr de vouloir supprimer le logo de l'établissement ?
            </p>
            <p className="text-gray-600">
              Le logo par défaut de NEXEDU sera affiché à la place.
            </p>
          </div>
        }
        confirmText="Oui, supprimer"
        cancelText="Annuler"
        onConfirm={handleDeleteLogo}
        onCancel={() => setDeleteLogoModal(false)}
        variant="danger"
      />

      {/* Modal: Demande de modification de solution */}
      {solutionModal.isOpen && solutionModal.solution && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setSolutionModal({ isOpen: false, solution: null, action: null })} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-full ${
                  solutionModal.action === 'ACTIVATE' ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                  {solutionModal.action === 'ACTIVATE' ? (
                    <Check className="w-6 h-6 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {solutionModal.action === 'ACTIVATE'
                      ? `Activer ${solutionConfig[solutionModal.solution].label}`
                      : `Desactiver ${solutionConfig[solutionModal.solution].label}`}
                  </h3>
                  <div className="text-sm text-gray-600">
                    {solutionModal.action === 'ACTIVATE' ? (
                      <div>
                        <p className="mb-3">
                          Vous etes sur le point de demander l'activation de la solution{' '}
                          <strong>{solutionConfig[solutionModal.solution].label}</strong>.
                        </p>
                        <p className="mb-3">
                          Cette demande sera envoyee au Super Admin pour validation. Vous recevrez une notification
                          lorsque votre demande sera traitee.
                        </p>
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800">
                          <strong>Note :</strong> Une fois approuvee, la solution sera immediatement disponible
                          pour tous les utilisateurs de votre etablissement.
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="mb-3">
                          Vous etes sur le point de demander la desactivation de la solution{' '}
                          <strong>{solutionConfig[solutionModal.solution].label}</strong>.
                        </p>
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 mb-3">
                          <strong>Important :</strong> Vos donnees seront conservees. Si vous reactivez cette solution
                          ulterieurement, vous retrouverez toutes vos informations.
                        </div>
                        <p>
                          Cette demande sera envoyee au Super Admin pour validation.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setSolutionModal({ isOpen: false, solution: null, action: null })}
                  disabled={submittingRequest}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmitSolutionRequest}
                  disabled={submittingRequest}
                  className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center gap-2 ${
                    solutionModal.action === 'ACTIVATE'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-amber-600 hover:bg-amber-700'
                  } disabled:opacity-50`}
                >
                  {submittingRequest ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    'Confirmer la demande'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Campus Form Component
interface CampusFormProps {
  form: CampusFormData;
  setForm: (form: CampusFormData) => void;
  showSigle: boolean;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  onMainCampusChange: (checked: boolean) => void;
  isFirstCampus: boolean;
  formErrors: Record<string, string>;
  setFormErrors: (errors: Record<string, string>) => void;
}

const CampusForm: React.FC<CampusFormProps> = ({
  form,
  setForm,
  showSigle,
  onSave,
  onCancel,
  saving,
  onMainCampusChange,
  isFirstCampus,
  formErrors,
  setFormErrors,
}) => {
  // Clear specific error when user starts typing
  const handleFieldChange = (field: keyof CampusFormData, value: string | boolean) => {
    setForm({ ...form, [field]: value });
    if (formErrors[field]) {
      const newErrors = { ...formErrors };
      delete newErrors[field];
      setFormErrors(newErrors);
    }
  };

  // Validate and save
  const handleSave = () => {
    const errors = validateCampusForm(form);
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      onSave();
    }
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom du campus *
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Ex: Campus Principal - Casablanca"
          />
          {formErrors.name && (
            <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>
          )}
        </div>

        {showSigle && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sigle du campus *
            </label>
            <input
              type="text"
              value={form.sigle}
              onChange={(e) => handleFieldChange('sigle', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Ex: HEMS-CASA"
            />
          </div>
        )}

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Adresse *
          </label>
          <input
            type="text"
            value={form.address}
            onChange={(e) => handleFieldChange('address', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formErrors.address ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Ex: 123 Boulevard Mohammed V"
          />
          {formErrors.address && (
            <p className="text-xs text-red-500 mt-1">{formErrors.address}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ville *
          </label>
          <input
            type="text"
            value={form.city}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formErrors.city ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Ex: Casablanca"
          />
          {formErrors.city && (
            <p className="text-xs text-red-500 mt-1">{formErrors.city}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Code Postal *
          </label>
          <input
            type="text"
            value={form.postalCode}
            onChange={(e) => handleFieldChange('postalCode', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formErrors.postalCode ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Ex: 20000"
          />
          {formErrors.postalCode && (
            <p className="text-xs text-red-500 mt-1">{formErrors.postalCode}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Ex: contact-casa@etablissement.ma"
          />
          {formErrors.email && (
            <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Numéro de réception *
          </label>
          <div className="flex gap-2">
            <select
              value={form.receptionPhoneCountry}
              onChange={(e) => handleFieldChange('receptionPhoneCountry', e.target.value)}
              className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              {COUNTRY_CODES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.code}
                </option>
              ))}
            </select>
            <input
              type="tel"
              value={form.receptionPhoneNumber}
              onChange={(e) => handleFieldChange('receptionPhoneNumber', e.target.value)}
              className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formErrors.receptionPhoneNumber ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Ex: 522123456"
            />
          </div>
          {formErrors.receptionPhoneNumber ? (
            <p className="text-xs text-red-500 mt-1">{formErrors.receptionPhoneNumber}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-500">Format: indicatif + numero sans le 0 initial</p>
          )}
        </div>
      </div>

      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.isMainCampus}
            onChange={(e) => onMainCampusChange(e.target.checked)}
            className="w-4 h-4 text-primary-600 focus:ring-primary-500 rounded"
          />
          <span className="text-sm text-gray-700">Définir comme campus principal</span>
          {isFirstCampus && (
            <span className="text-xs text-amber-600 ml-2">(Recommandé pour le premier campus)</span>
          )}
        </label>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={saving || !isCampusFormValid(form)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Annuler
        </button>
      </div>
    </div>
  );
};

export default GestionEtablissementPage;
